import React, { useMemo, useState } from 'react';

import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import { useGetAriRollup } from '~/hooks/apis/reporting/useAriAnomalies';

import { CaseScope, RollupDimension } from '~/services/AriAnomaliesService';

const DIMENSION_LABELS: Record<RollupDimension, string> = {
  channel: 'Channel',
  chain: 'Chain',
  vendorBrand: 'Vendor brand',
  territory: 'Territory',
};

interface Props {
  scope: CaseScope;
  /** hand off to the case list rather than duplicating triage here */
  onDrillIn: (dimension: RollupDimension, value: string) => void;
}

/**
 * Read-only. Aggregates the same cases the Cases tab works, so the two can never disagree,
 * and deliberately has no triage of its own - this answers "where is the damage
 * concentrated", then hands off. Everything gets actioned through a case.
 */
export default function CaseRollup({ onDrillIn, scope }: Props) {
  const [dimension, setDimension] = useState<RollupDimension>('vendorBrand');
  const { data: rollup = [], isLoading } = useGetAriRollup(dimension, scope);

  // most pending Tier 1 work first, then by how heavily the affected rates sell
  const rows = useMemo(
    () => [...rollup].sort((a, b) => b.tier1 - a.tier1 || b.bookingsExposed - a.bookingsExposed),
    [rollup],
  );

  return (
    <Box>
      <Alert severity="info" sx={{ mb: 2 }}>
        Where the damage is concentrated. This is a read-only view of the same cases — pick a row to take it into the
        case list, where the work gets done.
      </Alert>

      <FormControl size="small" sx={{ minWidth: 190, mb: 2 }}>
        <InputLabel id="rollup-dimension">Break down by</InputLabel>
        <Select
          labelId="rollup-dimension"
          label="Break down by"
          value={dimension}
          onChange={(e) => setDimension(e.target.value)}
        >
          {(Object.keys(DIMENSION_LABELS) as Array<RollupDimension>).map((d) => (
            <MenuItem key={d} value={d}>
              {DIMENSION_LABELS[d]}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{DIMENSION_LABELS[dimension]}</TableCell>
              <TableCell align="right">Pre-validated real</TableCell>
              <TableCell align="right">Tier 1</TableCell>
              <TableCell align="right">Bookings behind affected rates</TableCell>
              <TableCell align="right" />
            </TableRow>
          </TableHead>

          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.value} hover>
                <TableCell>
                  <Typography variant="body2" fontWeight={600}>
                    {r.value}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {r.cases} {r.cases === 1 ? 'case' : 'cases'}
                  </Typography>
                </TableCell>
                <TableCell align="right">{r.confirmed}</TableCell>
                <TableCell align="right">
                  {r.tier1 > 0 ? <Chip size="small" color="primary" label={r.tier1} /> : '—'}
                </TableCell>
                <TableCell align="right">{r.bookingsExposed.toLocaleString()}</TableCell>
                <TableCell align="right">
                  <Button size="small" endIcon={<ArrowForwardIcon />} onClick={() => onDrillIn(dimension, r.value)}>
                    Cases
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {isLoading && (
        <Stack alignItems="center" sx={{ mt: 2 }}>
          <CircularProgress size={24} />
        </Stack>
      )}

      <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
        <Typography variant="caption" color="text.secondary">
          &ldquo;Bookings behind affected rates&rdquo; is trailing 12-month volume on the rates in these cases — a
          measure of exposure, deliberately not a dollar estimate.
        </Typography>
      </Stack>
    </Box>
  );
}
